import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { Check, Clipboard } from 'react-bootstrap-icons';
import {
  ENCOUNTER_DECISION_OPTIONS,
  ENCOUNTER_NOTE_TYPE,
  ENCOUNTER_NOTE_TYPE_OPTIONS,
  regionLevelPlaceholder,
} from '../utils/jointdecEncounterNote';

function JointDecisionsEncounterNote({
  noteText,
  onNoteTextChange,
  decision,
  onDecisionChange,
  noteType = ENCOUNTER_NOTE_TYPE.SURGERY_DECISION,
  onNoteTypeChange,
  procedureLevelsText = '',
  onProcedureLevelsTextChange,
  regionLabel,
  showSurgeryDecisionControls = false,
  readOnly = false,
  hint,
  idPrefix = 'jd-encounter',
}) {
  const [copied, setCopied] = useState(false);
  const surgeryDecision = noteType === ENCOUNTER_NOTE_TYPE.SURGERY_DECISION;
  const hintText = hint || (readOnly ? 'Saved Joint Decisions encounter note.' : '');

  const handleCopy = async () => {
    const text = noteText || '';
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'absolute';
        area.style.left = '-9999px';
        document.body.appendChild(area);
        area.select();
        document.execCommand('copy');
        document.body.removeChild(area);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="jd-encounter-note">
      <div className="jd-encounter-note-toolbar">
        <Form.Group className="jd-encounter-note-decision mb-0">
          <Form.Label className="mb-0" htmlFor={`${idPrefix}-decision`}>
            Decision
          </Form.Label>
          <Form.Select
            id={`${idPrefix}-decision`}
            size="sm"
            value={decision}
            disabled={readOnly}
            onChange={(event) => onDecisionChange?.(event.target.value)}
            aria-label="Joint decision documented in the note"
          >
            {ENCOUNTER_DECISION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <div className="btn-group jd-encounter-note-type" role="group" aria-label="Note type">
          {ENCOUNTER_NOTE_TYPE_OPTIONS.map((option) => (
            <Button
              key={option.value}
              type="button"
              size="sm"
              variant={noteType === option.value ? 'primary' : 'outline-secondary'}
              className="jd-encounter-note-type-button"
              aria-pressed={noteType === option.value}
              onClick={() => onNoteTypeChange?.(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>
        <div className="jd-encounter-note-actions">
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={handleCopy}
            className="jd-encounter-note-copy"
          >
            {copied ? <Check size={16} /> : <Clipboard size={16} />}
            {copied ? 'Copied' : 'Copy note'}
          </Button>
        </div>
      </div>
      {showSurgeryDecisionControls && surgeryDecision ? (
        <div className="jd-encounter-note-clauses">
          <Form.Group className="jd-encounter-note-levels mb-0">
            <Form.Label className="mb-0" htmlFor={`${idPrefix}-levels`}>
              Levels
            </Form.Label>
            <Form.Control
              id={`${idPrefix}-levels`}
              size="sm"
              value={procedureLevelsText}
              disabled={readOnly}
              placeholder={regionLevelPlaceholder(regionLabel)}
              onChange={(event) => onProcedureLevelsTextChange?.(event.target.value)}
              aria-label="Anatomic levels for the surgery decision note"
            />
          </Form.Group>
        </div>
      ) : null}
      {hintText ? (
        <p className="jd-encounter-note-hint mb-0">
          {hintText}
        </p>
      ) : null}
      <label className="visually-hidden" htmlFor={`${idPrefix}-note-text`}>
        {surgeryDecision ? 'Surgery decision note' : 'Spine treatment encounter note'}
      </label>
      <textarea
        id={`${idPrefix}-note-text`}
        className="jd-encounter-note-text"
        value={noteText}
        readOnly={readOnly}
        onChange={readOnly ? undefined : (event) => onNoteTextChange?.(event.target.value)}
        spellCheck
      />
    </div>
  );
}

export default JointDecisionsEncounterNote;
